import React, { useEffect, useState } from 'react';
import { getShops, getFactories } from '../../services/api';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = [
  { value: 'NEW', label: '🆕 Νέα' },
  { value: 'IN_PROGRESS', label: '⚙️ Σε εξέλιξη' },
  { value: 'READY', label: '✅ Έτοιμη' },
  { value: 'DELIVERED', label: '📦 Παραδόθηκε' },
  { value: 'CANCELLED', label: '❌ Ακυρώθηκε' },
];

// Format YYYY-MM-DD for <input type="date">
const fmtDateInput = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function Export() {
  const [shops, setShops] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Defaults: from = first day of current month, to = today
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [from, setFrom] = useState(fmtDateInput(monthStart));
  const [to, setTo] = useState(fmtDateInput(today));
  const [statuses, setStatuses] = useState([]); // selected status array
  const [shopId, setShopId] = useState('');
  const [factoryId, setFactoryId] = useState('');

  useEffect(() => {
    Promise.all([getShops(false), getFactories(false)])
      .then(([sRes, fRes]) => {
        setShops(sRes.data);
        setFactories(fRes.data);
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Σφάλμα φόρτωσης'));
  }, []);

  const toggleStatus = (s) => {
    setStatuses((arr) => arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s]);
  };

  const doExport = async () => {
    if (!from || !to) return toast.error('Συμπληρώστε εύρος ημερομηνιών');
    if (from > to) return toast.error('Η ημερομηνία "Από" πρέπει να προηγείται της "Έως"');
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      // Treat date as local midnight; "to" date includes the entire day, so to+1 day at midnight
      const fromISO = new Date(from + 'T00:00:00').toISOString();
      const toDate = new Date(to + 'T00:00:00');
      toDate.setDate(toDate.getDate() + 1);
      params.set('from', fromISO);
      params.set('to', toDate.toISOString());
      if (statuses.length > 0) params.set('status', statuses.join(','));
      if (shopId) params.set('shop_id', shopId);
      if (factoryId) params.set('factory_id', factoryId);

      // Fetch as blob with auth token
      const token = localStorage.getItem('orders_token');
      const res = await fetch(`/api/orders/export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!res.ok) {
        let errMsg = 'Σφάλμα εξαγωγής';
        try {
          const errJson = await res.json();
          if (errJson.error) errMsg = errJson.error;
        } catch (e) {}
        throw new Error(errMsg);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paraggelies_${from}_${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Το αρχείο κατεβάστηκε');
    } catch (err) {
      toast.error(err.message || 'Σφάλμα εξαγωγής');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h2>📤 Εξαγωγή Παραγγελιών</h2>
        <p>Δημιουργία Excel αρχείου με τις παραγγελίες</p>
      </div>
      <div className="page-body">
        <div className="card" style={{ maxWidth: 600 }}>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Από</label>
                <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Έως</label>
                <input type="date" className="form-input" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Κατάσταση (κενό = όλες)</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {STATUS_OPTIONS.map((s) => (
                  <label key={s.value} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    border: '1px solid var(--gray-200)',
                    borderRadius: 6,
                    background: statuses.includes(s.value) ? 'var(--blue-light)' : 'white',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}>
                    <input type="checkbox" checked={statuses.includes(s.value)} onChange={() => toggleStatus(s.value)} style={{ margin: 0 }} />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Κατάστημα</label>
                <select className="form-select" value={shopId} onChange={(e) => setShopId(e.target.value)}>
                  <option value="">Όλα</option>
                  {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Εργοστάσιο</label>
                <select className="form-select" value={factoryId} onChange={(e) => setFactoryId(e.target.value)}>
                  <option value="">Όλα</option>
                  {factories.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
            </div>

            <div style={{ background: 'var(--gray-50)', padding: 12, borderRadius: 8, fontSize: 12, color: 'var(--gray-600)', marginBottom: 16 }}>
              💡 Το αρχείο Excel θα περιέχει 2 φύλλα: σύνοψη παραγγελιών + λεπτομέρειες προϊόντων. Μέγιστο 5000 παραγγελίες ανά εξαγωγή.
            </div>

            <button className="btn btn-primary btn-block" onClick={doExport} disabled={loading}>
              {loading ? <span className="loading-spinner" /> : '📥 Λήψη αρχείου Excel'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
